import React, { useState, useEffect, useCallback } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

interface Screenshot {
  src: string;
  title: string;
  description: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface Step {
  number: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface PainPoint {
  icon: React.ReactNode;
  title: string;
  description: string;
}

// Screenshots data with labels
const screenshots: Screenshot[] = [
  { src: '/images/revision/manuals-app-student-dashboard.webp', title: 'Dashboard', description: "Vue d'ensemble des manuels disponibles" },
  { src: '/images/revision/manuals-app-student-manual.webp', title: 'Manuels', description: 'Sélection du manuel scolaire' },
  { src: '/images/revision/manuals-app-student-manual-page.webp', title: 'Page manuel', description: 'Lecture interactive de la leçon' },
  { src: '/images/revision/manuals-app-student-manual-activity-tat.webp', title: 'Activité', description: 'Exercices interactifs et quiz' },
  { src: '/images/revision/manuals-app-student-communication.webp', title: 'Communication', description: 'Interface de chat avec Lumi' },
  { src: '/images/revision/manuals-app-student-communication-en-cours.webp', title: 'Conversation', description: 'Discussion en cours avec Lumi' },
  { src: '/images/revision/manuals-app-student-communication-synthese.webp', title: 'Synthèse', description: 'Résumé de la conversation' },
  { src: '/images/revision/manuals-app-student-profil.webp', title: 'Profil', description: "Compte et paramètres de l'élève" },
  { src: '/images/revision/manuals-app-student-profil-badges.webp', title: 'Badges', description: 'Récompenses et succès obtenus' },
  { src: '/images/revision/manuals-app-student-profil-classement.webp', title: 'Classement', description: 'Tableau des scores et progression' },
];

// FAQ data
const faqItems: FAQItem[] = [
  {
    question: 'Mon enfant va-t-il tricher avec Lumi?',
    answer: "Non. Lumi utilise la méthode Socratique: il ne donne jamais les réponses directement. Il pose des questions guidées qui aident votre enfant à réfléchir et découvrir la solution par lui-même. Votre enfant apprend à penser, pas à copier."
  },
  {
    question: 'Est-ce que Lumi remplace les enseignants?',
    answer: "Absolument pas. Lumi est un complément au travail de l'enseignant, pas un remplacement. Il reprend les mêmes leçons du manuel pour offrir du renforcement supplémentaire à la maison."
  },
  {
    question: 'Combien ça coûte?',
    answer: "C'est inclus dans la scolarité. Révision fait partie des services offerts par Groupe Scolaire Sanaa. Aucun frais supplémentaire."
  },
  {
    question: 'Sur quels appareils ça fonctionne?',
    answer: "Téléphone, tablette ou ordinateur. Révision fonctionne dans le navigateur web. Aucune installation requise."
  },
  {
    question: 'Quelles matières sont couvertes?',
    answer: "Arabe, Français, Anglais, Mathématiques et Sciences. Du CP au CE6, tous les manuels scolaires sont disponibles."
  },
  {
    question: 'Comment mon enfant se connecte-t-il?',
    answer: "Avec ses identifiants Google Classroom - les mêmes qu'il utilise à l'école. C'est un espace sans jugement où votre enfant peut poser la même question 10 fois sans gêne, avancer à son propre rythme, et faire des erreurs en toute sécurité. Lumi ne juge jamais."
  },
];

// Features data
const features: Feature[] = [
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
      </svg>
    ),
    title: 'Tuteur IA Personnel',
    description: "Lumi explique chaque leçon du manuel, répond aux questions, et s'adapte au rythme de votre enfant."
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
      </svg>
    ),
    title: 'Narration Audio',
    description: "Chaque page est lue à voix haute par une voix professionnelle. Idéal pour les apprenants auditifs."
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.39 48.39 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" />
      </svg>
    ),
    title: 'Activités Interactives',
    description: "Quiz, exercices à trous, associations... Votre enfant apprend en s'amusant tout en vérifiant sa compréhension."
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
      </svg>
    ),
    title: 'Accès Simple',
    description: "Pas d'installation, pas de téléchargement. Ouvrez le manuel, entrez le numéro de page, Lumi s'occupe du reste."
  },
];

// How it works steps
const steps: Step[] = [
  {
    number: '1',
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
      </svg>
    ),
    title: 'Ouvrez le manuel',
    description: "Votre enfant prend son manuel scolaire habituel - celui utilisé en classe."
  },
  {
    number: '2',
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.25 8.25h15m-16.5 7.5h15m-1.8-13.5l-3.9 19.5m-2.1-19.5l-3.9 19.5" />
      </svg>
    ),
    title: 'Entrez le numéro de page',
    description: "Sur revision.gssanaa.com, il saisit simplement le numéro de la page qu'il veut réviser."
  },
  {
    number: '3',
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
      </svg>
    ),
    title: 'Lumi explique!',
    description: "Lumi présente la leçon, lit le texte, répond aux questions et propose des activités."
  },
];

// Pain points data
const painPoints: PainPoint[] = [
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
      </svg>
    ),
    title: 'Fini les soirées de devoirs stressantes',
    description: "Lumi prend le relais quand vous n'avez plus la patience ou le temps. Il explique chaque leçon avec douceur et à son rythme."
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
      </svg>
    ),
    title: 'Du temps d\'écran qui compte vraiment',
    description: "Transformez le temps d'écran de votre enfant en apprentissage actif. Lumi ne distrait jamais, il enseigne."
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
      </svg>
    ),
    title: 'Lumi explique, ré-explique, sans jamais se lasser',
    description: "Votre enfant peut poser la même question 10 fois. Lumi ne juge pas, ne s'impatiente pas, et trouve toujours un nouvel angle."
  },
];

// Carousel Component
const ScreenshotCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % screenshots.length);
  }, []);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [isPaused, nextSlide]);

  return (
    <div
      className="relative w-full max-w-4xl mx-auto"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Main carousel container */}
      <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-gray-900">
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {screenshots.map((screenshot, index) => (
            <div key={index} className="w-full flex-shrink-0 aspect-[16/10] flex items-center justify-center bg-gray-900">
              <img
                src={screenshot.src}
                alt={screenshot.title}
                loading="lazy"
                className="w-full h-full object-contain"
              />
            </div>
          ))}
        </div>

        {/* Navigation arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
          aria-label="Previous screenshot"
        >
          <svg className="w-5 h-5 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all"
          aria-label="Next screenshot"
        >
          <svg className="w-5 h-5 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Caption */}
      <div className="text-center mt-6">
        <h4 className="text-xl font-semibold text-gray-900">{screenshots[currentIndex].title}</h4>
        <p className="text-gray-600 mt-1">{screenshots[currentIndex].description}</p>
      </div>

      {/* Dots navigation */}
      <div className="flex justify-center gap-2 mt-4">
        {screenshots.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all ${
              index === currentIndex
                ? 'bg-[#00A3FF] w-8'
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
            aria-label={`Go to screenshot ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// FAQ Accordion Component
const FAQAccordion = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      {faqItems.map((item, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-xl overflow-hidden"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full px-6 py-4 text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
          >
            <span className="font-semibold text-gray-900">{item.question}</span>
            <svg
              className={`w-5 h-5 text-[#00A3FF] transition-transform ${openIndex === index ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div
            className={`overflow-hidden transition-all duration-300 ${
              openIndex === index ? 'max-h-96' : 'max-h-0'
            }`}
          >
            <p className="px-6 pb-4 text-gray-600">{item.answer}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Stagger container animation variants
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

// Main Page Component
const RevisionPage = () => {
  const { scrollYProgress } = useScroll();
  const heroParallaxY = useTransform(scrollYProgress, [0, 0.3], [0, -80]);
  const heroDecoScale = useTransform(scrollYProgress, [0, 0.2], [1, 1.15]);
  const heroDecoOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">

      {/* ===== 1. HERO SECTION ===== */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#00A3FF] via-[#0090E0] to-[#00D4FF]" />

        {/* Decorative elements with parallax */}
        <motion.div
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{ scale: heroDecoScale, opacity: heroDecoOpacity }}
        >
          <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-300/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl" />
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="hero-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <circle cx="20" cy="20" r="1" fill="white" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#hero-grid)" />
            </svg>
          </div>
        </motion.div>

        <motion.div
          className="relative container mx-auto px-4"
          style={{ y: heroParallaxY }}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Sparkle / AI Icon */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30 mb-8"
            >
              <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
              </svg>
            </motion.div>

            {/* Main Heading — parent pain point */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
            >
              Votre enfant n'est plus jamais seul face à ses devoirs
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-8"
            >
              Lumi est le tuteur IA personnel qui accompagne votre enfant du CP au CE6, dans toutes les matières, à tout moment.
            </motion.p>

            {/* Subjects badge */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.55 }}
              className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-6 py-2.5 mb-10"
            >
              <svg className="w-4 h-4 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
              </svg>
              <span className="text-sm font-medium text-white/90 tracking-wide">
                Arabe · Français · Anglais · Maths · Sciences
              </span>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.65 }}
            >
              <a
                href="https://revision.gssanaa.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-[#00A3FF] font-bold px-10 py-4 rounded-full shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-black/15 transition-all duration-300 hover:scale-105 text-lg"
              >
                Se connecter
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
              <p className="text-white/60 text-sm mt-4">
                Utilisez les identifiants Google Classroom de votre enfant
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== 2. PAIN POINTS SECTION ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-sm font-semibold text-[#00A3FF] tracking-widest uppercase mb-3">
              Pour les parents
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Pourquoi les parents adorent Lumi
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
          >
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="relative group"
              >
                <div className="bg-gradient-to-br from-[#F0F9FF] to-white border border-[#BAE6FD]/40 rounded-2xl p-8 h-full transition-all duration-300 hover:shadow-xl hover:shadow-[#00A3FF]/8 hover:border-[#00A3FF]/30">
                  {/* Icon */}
                  <div className="w-14 h-14 bg-gradient-to-br from-[#00A3FF] to-[#00D4FF] rounded-xl flex items-center justify-center text-white mb-6 shadow-md shadow-[#00A3FF]/20 group-hover:scale-110 transition-transform duration-300">
                    {point.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug">{point.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{point.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== 3. HOW IT WORKS SECTION ===== */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-sm font-semibold text-[#00A3FF] tracking-widest uppercase mb-3">
              Comment ça marche
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              En 3 étapes simples
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Découvrez comment votre enfant peut commencer à apprendre avec Lumi dès maintenant.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {steps.map((step, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="relative text-center"
              >
                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-1/2 w-full h-0.5 bg-gradient-to-r from-[#00A3FF] to-[#00D4FF] opacity-30" />
                )}

                <div className="relative z-10 inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-[#00A3FF] to-[#00D4FF] rounded-3xl text-white mb-6 shadow-lg shadow-[#00A3FF]/20">
                  {step.icon}
                  <span className="absolute -top-2 -right-2 w-8 h-8 bg-white text-[#00A3FF] rounded-full flex items-center justify-center font-bold shadow-md text-sm">
                    {step.number}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== 4. FEATURES SECTION ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-sm font-semibold text-[#00A3FF] tracking-widest uppercase mb-3">
              Fonctionnalités
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Tout ce dont votre enfant a besoin
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              5 matières couvertes: Arabe, Français, Anglais, Mathématiques et Sciences. Du CP au CE6.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="group bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:shadow-[#00A3FF]/8 hover:border-[#00A3FF]/20 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#E0F2FE] to-[#BAE6FD] rounded-xl flex items-center justify-center text-[#00A3FF] mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== 5. VIDEO TUTORIALS SECTION ===== */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-block text-sm font-semibold text-[#00A3FF] tracking-widest uppercase mb-3">
              Guides
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Tutoriaux vidéo
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Regardez nos guides vidéo pour apprendre à utiliser Révision avec votre enfant.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto"
          >
            {/* Video 1 - Révision Manuel */}
            <motion.div variants={staggerItem} className="flex flex-col">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-lg shadow-black/10 bg-gray-900 ring-1 ring-gray-200">
                <video
                  controls
                  preload="metadata"
                  className="w-full h-full object-contain"
                >
                  <source src="/videos/lumi-revision-manuel-tutoriel.mp4" type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              </div>
              <div className="mt-4 text-center">
                <h4 className="text-lg font-semibold text-gray-900">Réviser avec le manuel</h4>
                <p className="text-gray-600 text-sm mt-1">Découvrez comment utiliser Lumi pour réviser les leçons du manuel scolaire</p>
              </div>
            </motion.div>

            {/* Video 2 - Communication */}
            <motion.div variants={staggerItem} className="flex flex-col">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-lg shadow-black/10 bg-gray-900 ring-1 ring-gray-200">
                <video
                  controls
                  preload="metadata"
                  className="w-full h-full object-contain"
                >
                  <source src="/videos/lumi-communication-tutoriel.mp4" type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              </div>
              <div className="mt-4 text-center">
                <h4 className="text-lg font-semibold text-gray-900">Communication orale avec Lumi</h4>
                <p className="text-gray-600 text-sm mt-1">Découvrez les séances de communication orale en arabe, français et anglais</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== 6. SCREENSHOTS CAROUSEL SECTION ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-block text-sm font-semibold text-[#00A3FF] tracking-widest uppercase mb-3">
              Aperçu
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Découvrez l'application
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Explorez les différentes fonctionnalités de Révision à travers ces captures d'écran.
            </p>
          </motion.div>

          <ScreenshotCarousel />
        </div>
      </section>

      {/* ===== 7. RESOURCES SECTION ===== */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto text-center"
          >
            <span className="inline-block text-sm font-semibold text-[#00A3FF] tracking-widest uppercase mb-3">
              Ressources
            </span>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Ressources pour les parents
            </h3>
            <a
              href="/documents/guide-parents-revision.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white border-2 border-[#00A3FF] text-[#00A3FF] px-6 py-4 rounded-xl hover:bg-[#00A3FF] hover:text-white transition-all duration-300 group shadow-sm hover:shadow-md"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              <div className="text-left">
                <p className="font-bold">Guide pour les parents</p>
                <p className="text-sm opacity-80">Télécharger le PDF complet</p>
              </div>
            </a>
          </motion.div>
        </div>
      </section>

      {/* ===== 8. FAQ SECTION ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-block text-sm font-semibold text-[#00A3FF] tracking-widest uppercase mb-3">
              FAQ
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Questions fréquentes
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Trouvez les réponses aux questions les plus courantes des parents.
            </p>
          </motion.div>

          <FAQAccordion />
        </div>
      </section>

      {/* ===== 9. FOOTER CTA SECTION ===== */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#00A3FF] via-[#0090E0] to-[#00D4FF]" />

        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-cyan-300/10 rounded-full blur-3xl" />
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <circle cx="20" cy="20" r="1" fill="white" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#cta-grid)" />
            </svg>
          </div>
        </div>

        <div className="relative container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            {/* Mini Lumi avatar */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 mb-6"
            >
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              </svg>
            </motion.div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Prêt à commencer?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">
              Révision est disponible maintenant pour tous les élèves de Groupe Scolaire Sanaa.
            </p>

            <a
              href="https://revision.gssanaa.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#00A3FF] font-bold px-10 py-4 rounded-full shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-black/15 transition-all duration-300 hover:scale-105 text-lg"
            >
              Connectez-vous maintenant
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>

            <p className="text-white/60 text-sm mt-6">
              Identifiants Google Classroom requis · Des questions? Contactez l'administration de Groupe Scolaire Sanaa.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RevisionPage;
